import { useCallback, useEffect, useState } from "react";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import GroupsIcon from "@mui/icons-material/Groups";
import SaveIcon from "@mui/icons-material/Save";
import CircularProgress from "@mui/material/CircularProgress";
import SnackBar from "../../components/SnackBar";
import {
  atualizarTributariasParametro,
  buscarTributariasParametros,
} from "../../services/api-relatorios.service";
import {
  formatarCampoTributaria,
  mascararCampoTributaria,
  parseValorNumerico,
} from "./tributarias.util";
import styles from "./styles.module.css";

/**
 * TXM-336 (T2) — Parametrizações → Tributárias.
 * Protótipo: definicao/esocial-tributacoes.txt.
 * Params globais do Cálculo 20S (`valor_diferenca_base`, `taxa_20s`),
 * sem override por CNPJ (D14). Alterações entram em vigor no próximo
 * reprocessamento da apuração eSocial.
 */
const CAMPOS = [
  {
    chave: "valor_diferenca_base",
    id: "varValorDiferencaBase",
    label: "Valor Diferença Base",
    ajuda: "Valor monetário de referência para diferença de base previdenciária.",
    placeholder: "0,00",
  },
  {
    chave: "taxa_20s",
    id: "varTaxa20S",
    label: "Taxa 20S",
    ajuda: "Percentual aplicado na regra 20S (folha / eSocial).",
    placeholder: "5,80%",
  },
];

const Tributarias = () => {
  const [valores, setValores] = useState({});
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");
  const [objSnackBar, setObjSnackBar] = useState({ type: "", msg: "" });

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro("");
    try {
      const res = await buscarTributariasParametros();
      const mapa = {};
      for (const p of res?.parametros ?? []) {
        mapa[p.chave] = formatarCampoTributaria(p.chave, p.valor);
      }
      setValores(mapa);
    } catch (e) {
      setErro(
        e?.code === "ECONNABORTED" || e?.message?.includes("timeout")
          ? "Tempo esgotado ao carregar parâmetros. Verifique a API de autenticação e o banco."
          : e?.response?.data?.mensagem ||
              e?.response?.data?.error ||
              e?.message ||
              "Erro ao carregar parâmetros."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const handleChange = (chave, raw) => {
    setValores((prev) => ({
      ...prev,
      [chave]: mascararCampoTributaria(chave, raw),
    }));
  };

  const handleBlur = (chave) => {
    setValores((prev) => {
      const n = parseValorNumerico(prev[chave]);
      if (!Number.isFinite(n)) return prev;
      return { ...prev, [chave]: formatarCampoTributaria(chave, n) };
    });
  };

  const handleSalvar = async () => {
    const parseados = {};
    for (const campo of CAMPOS) {
      const valor = parseValorNumerico(valores[campo.chave]);
      if (!Number.isFinite(valor)) {
        setObjSnackBar({
          type: "error",
          msg: `Valor inválido em "${campo.label}".`,
        });
        return;
      }
      parseados[campo.chave] = valor;
    }

    setSalvando(true);
    try {
      const resultados = await Promise.all(
        CAMPOS.map((campo) =>
          atualizarTributariasParametro(campo.chave, parseados[campo.chave])
        )
      );
      const falha = resultados.find((r) => r && r.sucesso === false);
      if (falha) {
        setObjSnackBar({
          type: "error",
          msg: falha.mensagem || "Não foi possível salvar os parâmetros.",
        });
        return;
      }
      setValores((prev) => {
        const next = { ...prev };
        for (const campo of CAMPOS) {
          next[campo.chave] = formatarCampoTributaria(
            campo.chave,
            parseados[campo.chave]
          );
        }
        return next;
      });
      setObjSnackBar({ type: "success", msg: "Variáveis salvas com sucesso." });
    } catch (e) {
      setObjSnackBar({
        type: "error",
        msg:
          e?.response?.data?.mensagem ||
          e?.response?.data?.error ||
          e?.message ||
          "Erro ao salvar.",
      });
    } finally {
      setSalvando(false);
    }
  };

  const camposBloqueados = loading || salvando;

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>
          <AccountBalanceIcon className={styles.pageTitleIcon} aria-hidden />
          Tributárias
        </h1>
        <p className={styles.pageSubtitle}>
          Parametrizações de variáveis tributárias e integrações previdenciárias.
        </p>
      </header>

      {erro && (
        <div className={styles.erro} role="alert">
          {erro}
          <button type="button" className={styles.btnRetry} onClick={carregar}>
            Tentar novamente
          </button>
        </div>
      )}

      {!erro && (
        <section className={styles.configSection} aria-labelledby="secEsocialTitulo">
          <div className={styles.configSectionHeader}>
            <div className={styles.configSectionIcon} aria-hidden>
              <GroupsIcon className={styles.configSectionIconSvg} />
            </div>
            <div>
              <h2 id="secEsocialTitulo">E-Social</h2>
              <p>Variáveis de sistema utilizadas nos cálculos e importações de folha.</p>
            </div>
          </div>
          <div className={styles.configSectionBody}>
            <p className={styles.varsIntro}>
              Defina os valores padrão aplicados em todo o ambiente quando não houver
              override por cliente.
            </p>
            <div className={styles.varsGrid}>
              {CAMPOS.map((campo) => (
                <div key={campo.chave} className={styles.formField}>
                  <label htmlFor={campo.id}>{campo.label}</label>
                  <input
                    id={campo.id}
                    name={campo.chave}
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    placeholder={campo.placeholder}
                    value={valores[campo.chave] ?? ""}
                    disabled={camposBloqueados}
                    aria-busy={loading}
                    onChange={(e) => handleChange(campo.chave, e.target.value)}
                    onBlur={() => handleBlur(campo.chave)}
                  />
                  <p className={styles.formHint}>{campo.ajuda}</p>
                </div>
              ))}
            </div>
            <div className={styles.formActions}>
              <button
                type="button"
                className={styles.btnSalvar}
                disabled={camposBloqueados}
                onClick={handleSalvar}
              >
                <SaveIcon className={styles.btnSalvarIcon} aria-hidden />
                {salvando ? "Salvando…" : "Salvar"}
              </button>
              {loading && (
                <span className={styles.loadingInline} aria-live="polite">
                  <CircularProgress size={18} thickness={5} />
                  Carregando…
                </span>
              )}
            </div>
          </div>
        </section>
      )}

      <SnackBar objSnackBar={objSnackBar} setObjSnackBar={setObjSnackBar} />
    </div>
  );
};

export default Tributarias;
